import useTranslate from '@hooks/useTranslate';
import SCREENS from '@constants/Screens';
import Screen from '@ui/templates/Screen';
import { useNavigation } from '@react-navigation/native';
import useToast from '@hooks/useToast';
import { useFormik } from 'formik';
import { object, string } from '@utils/yup';
import apiErrorsToFormik from '@utils/apiErrorsToFormik';
import React, { useState } from 'react';
import { Button, Input, View, Text, Pressable } from '@ui/atoms';
import { FormControl } from '@ui/molecules';
import useInviteUserMutation from '@hooks/mutations/useInviteUserMutation';
import useCurrentResidenceJoinCodeQuery from '@hooks/queries/useCurrentResidenceJoinQuery';
import { Platform, Share } from 'react-native';

function InviteUserScreen() {
  const toast = useToast();
  const { t } = useTranslate();
  const { goBack } = useNavigation();

  const { mutateAsync } = useInviteUserMutation();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: residenceCode, isFetching } = useCurrentResidenceJoinCodeQuery();

  const isWeb = Platform.OS === 'web';

  const handleCopyOrShare = async () => {
    if (!residenceCode) {
      toast.error(t('InviteUserScreen.noResidenceCode') || 'Aucun code de résidence disponible');
      return;
    }
    try {
      const text = String(residenceCode);
      if (isWeb && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(t('InviteUserScreen.copied') || 'Code copié');
      } else {
        await Share.share({ message: text });
        toast.success(t('InviteUserScreen.shared') || 'Code prêt à partager');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error(t('errors.copyFailed') || 'Impossible de copier/partager le code');
    }
  };

  const form = useFormik({
    initialValues: { firstname: '', lastname: '', email: '' },
    validationSchema: object({
      firstname: string().required(),
      lastname: string().required(),
      email: string().email().required(),
    }),
    onSubmit: async (data, { setErrors }) => {
      try {
        setLoading(true);
        await mutateAsync(data);
        toast.success(t('InviteUserScreen.success'));
        goBack();
      } catch (err) {
        if (422 === err?.response?.status) {
          apiErrorsToFormik(err.response.data.errors, setErrors);
          toast.error(t('errors.checkData'));
        } else {
          toast.error(t('errors.api.default'));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Screen title={t('InviteUserScreen.title')} presentation={InviteUserScreen.presentation} scrollable>
      <View>
        {/* ===== Code de la résidence (centré) ===== */}
        <View
          sx={{
            mb: '$6',
            p: '$4',
            borderWidth: 1,
            borderColor: '$border.default',
            borderRadius: 12,
            backgroundColor: '$background.raised',
            alignItems: 'center',
          }}
        >
          <Text variant="overline" sx={{ mb: '$3', color: '$text.secondary', textAlign: 'center' }}>
            {t('InviteUserScreen.residenceCodeTitle') || 'Code de la résidence'}
          </Text>

          {/* Ruban de cases (tap-to-copy sur tout le ruban) */}
          <Pressable
            onPress={residenceCode ? handleCopyOrShare : undefined}
            disabled={!residenceCode || isFetching}
            accessibilityLabel={t('InviteUserScreen.copy') || 'Copier le code'}
          >
            <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
              {String(residenceCode || '')
                .split('')
                .map((ch, idx, arr) => (
                  <View
                    key={`${idx}-${ch}`}
                    sx={{
                      width: 44,
                      height: 50,
                      borderWidth: 1,
                      borderColor: '$border.default',
                      borderRadius: 10,
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: idx < arr.length - 1 ? '$2' : 0,
                      backgroundColor: '$background.body',
                    }}
                  >
                    <Text variant="title3" sx={{ fontWeight: '700' }}>
                      {ch}
                    </Text>
                  </View>
                ))}
            </View>
          </Pressable>

          {/* Mobile: helper text / Web: bouton primaire CENTRÉ */}
          {isWeb ? (
            <View sx={{ mt: '$3', alignItems: 'center' }}>
              <Button
                color="primary"
                onPress={handleCopyOrShare}
                title={
                  copied ? t('InviteUserScreen.copied') || 'Copié' : t('InviteUserScreen.copy') || 'Copier le code'
                }
                disabled={!residenceCode || isFetching}
                testID="copy-residence-code"
              />
            </View>
          ) : (
            <Text sx={{ mt: '$2', color: '$text.secondary', textAlign: 'center' }}>
              {copied
                ? t('InviteUserScreen.copied') || 'Code copié'
                : t('InviteUserScreen.tapToCopy') || 'Touchez « Copier le code »'}
            </Text>
          )}
        </View>

        {/* ===== Séparateur "OU" ===== */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginVertical: 16, // espace vertical
          }}
        >
          <View style={{ flex: 1, height: 1, backgroundColor: '#ccc' }} />
          <Text
            style={{
              marginHorizontal: 8,
              color: '#666',
              textAlign: 'center',
            }}
          >
            OU
          </Text>
          <View style={{ flex: 1, height: 1, backgroundColor: '#ccc' }} />
        </View>

        {/* ===== Formulaire d’invitation ===== */}
        <FormControl
          label={t('InviteUserScreen.firstname')}
          sx={{ mb: '$4' }}
          error={form.touched.firstname && form.errors.firstname}
          required
        >
          <Input
            value={form.values.firstname}
            onChangeText={form.handleChange('firstname')}
            onBlur={form.handleBlur('firstname')}
          />
        </FormControl>

        <FormControl
          label={t('InviteUserScreen.lastname')}
          sx={{ mb: '$4' }}
          error={form.touched.lastname && form.errors.lastname}
          required
        >
          <Input
            value={form.values.lastname}
            onChangeText={form.handleChange('lastname')}
            onBlur={form.handleBlur('lastname')}
          />
        </FormControl>

        <FormControl
          label={t('InviteUserScreen.email')}
          sx={{ mb: '$4' }}
          error={form.touched.email && form.errors.email}
          required
        >
          <Input
            value={form.values.email}
            onChangeText={form.handleChange('email')}
            onBlur={form.handleBlur('email')}
          />
        </FormControl>

        <Button color="primary" onPress={form.submitForm} loading={loading} title={t('InviteUserScreen.submit')} />
      </View>
    </Screen>
  );
}

InviteUserScreen.presentation = 'modal';
InviteUserScreen.screenName = SCREENS.inviteUser;

export default InviteUserScreen;
